// src/components/upmode-agent/confirm-dialog.tsx
import React from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./alert-dialog";

export interface ConfirmDialogProps {
  /** Controls visibility. Parent owns this state. */
  open: boolean;
  /** Fired when the dialog closes (either via cancel, outside click, or after confirm). */
  onOpenChange: (open: boolean) => void;
  /** Fired when the user clicks the confirm action. */
  onConfirm: () => void;
  title: string;
  description?: React.ReactNode;
  /** Confirm button label. Default: "Confirm" */
  confirmLabel?: string;
  /** Cancel button label. Default: "Cancel" */
  cancelLabel?: string;
  /** Styles the confirm button in a destructive red when true. Default: false */
  destructive?: boolean;
}

/**
 * Thin wrapper around shadcn AlertDialog that gives us a single, consistent
 * "are you sure?" confirmation flow used across the Upmode Agent UI. Replaces
 * native window.confirm() calls so modals match the rest of the design system.
 */
export function ConfirmDialog({
  open,
  onOpenChange,
  onConfirm,
  title,
  description,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  destructive = false,
}: ConfirmDialogProps) {
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          {description && (
            <AlertDialogDescription>{description}</AlertDialogDescription>
          )}
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>{cancelLabel}</AlertDialogCancel>
          <AlertDialogAction
            onClick={onConfirm}
            className={
              destructive
                ? "bg-destructive text-destructive-foreground hover:bg-destructive/90"
                : undefined
            }
          >
            {confirmLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}